import { useCallback, useRef } from 'react';

import customNanoId from 'lib/nanoid/nanoid';
import ActivitySheet from '../../shared/sheet/activity-sheet';
import ActivitySheetSaveStatus from '../../shared/sheet/activity-sheet-save-status';
import SubActivityView from '../../sub-activity/sub-activity-view';
import {
  useInteractiveAudio,
  useInteractiveModal,
} from '../shared/interactive-response-shared.hooks';
import { InteractiveResponseContainer } from '../interactive-response.style';
import { ViewProps } from './settings/interactive-response.constants';
import InteractiveResponseStart from '../shared/interactive-response-start';
import { MEDIA_SM, useMediaQuery } from 'approot/shared/style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { getComponentSelectorId } from '../../shared/logic/get-component-selector-id';
import InteractiveDragDrop from '../../shared/interactive-drag-drop/interactive-drag-drop';
import InteractiveSequenceLargeView from './interactive-sequence-large-view';
import InteractiveSequenceSmallView from './interactive-sequence-small-view';
import { useBatchStudentAnswersContext } from '../../shared/batch-student-answer.context';
import { useStudentGetAnswers } from 'approot/activity-builder/student/answer/student-answer.hooks';
import {
  DragItem,
  DropItem,
} from '../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import type { InteractiveResponseAnswerFragment } from '../../../present/activity-answers.graphql.generated';
import { ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE } from 'global/constants';
import {
  ActivitySheetInnerContent,
  ActivitySheetInnerMax,
} from '../../shared/sheet/activity-sheet.style';
import InteractiveResponseSequenceAnswers from './interactive-response-sequence-answers';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';

const InteractiveResponseStudent = (
  props: ViewProps & {
    isLoggedIn: boolean;
  }
) => {
  const { id, type, subActivityProperties, isLoggedIn } = props;

  const closeModalCallbackRef = useRef<Function | undefined>(undefined);

  const isMediaUpSm = useMediaQuery(MEDIA_SM);
  const { isPlaying, startGame, finishGame } = useInteractiveModal();
  const { openModal, closeModal, isVisible } = useSimpleModalState();
  const { toggleAudio, playAudio, playClick, playStart } =
    useInteractiveAudio();

  const { isLesson, setActiveComponent } = useActivityBuilderPresentContext();

  const { componentAnswers, variables, hasFetchedAnswers, error } =
    useStudentGetAnswers({
      componentId: id,
      skip: !isLoggedIn,
    });

  const { handleUpdate } = useBatchStudentAnswersContext();

  const handleSaveAttempt = useCallback(
    (dropItems: DropItem[], dragItems: DragItem[]) => {
      if (
        isLoggedIn &&
        subActivityProperties?.activityComponentId &&
        variables
      ) {
        handleUpdate({
          variables,
          dataTrackingComponentType:
            ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE,
          input: {
            __typename: 'ActivityAnswer',
            uuid: customNanoId(),
            activityId: subActivityProperties?.activityComponentId,
            componentId: id,
            createdAt: new Date(),
            updatedAt: null,
            studentId: null,
            answerReferences: null,
            answerType: 'interactive-response-answer',
            answer: {
              __typename: 'InteractiveResponseAnswer',
              interactiveResponseItems: dragItems.map(drag => {
                const dragItemDestination = dropItems.find(
                  drop => drop.zones[0].dragItemId === drag.id
                );
                return {
                  __typename: 'InteractiveResponseAnswerItem',
                  imageId: drag.question.image?.itemId || '',
                  dropZoneId: dragItemDestination?.id || '',
                  isCorrect: !!dragItemDestination?.zones[0].isCorrect,
                };
              }),
            },
          },
        });
      }
    },
    [id, subActivityProperties, variables, isLoggedIn, handleUpdate]
  );

  return (
    <>
      <SubActivityView
        subActivityDetails={subActivityProperties}
        componentId={id}
        componentType={type}
        onClick={() => setActiveComponent({ id })}
      >
        <InteractiveResponseContainer id={getComponentSelectorId(id)}>
          <InteractiveResponseSequenceAnswers
            {...props}
            isLoading={isLoggedIn && !hasFetchedAnswers}
            answerAttempt={componentAnswers?.[0]}
            showAnswer={isLesson}
          />
          <InteractiveResponseStart
            isLoggedIn={isLoggedIn}
            hasFetchedAnswers={hasFetchedAnswers}
            error={error}
            label="Matching"
            title={props.properties.interactiveResponse.title || ''}
            onStart={() => {
              openModal();
              startGame();
            }}
          />
        </InteractiveResponseContainer>
      </SubActivityView>
      {isVisible && (
        <ActivitySheet
          isVisible
          hasBackgroundColor
          title={props.properties.interactiveResponse.title || ''}
          customActions={
            <ActivitySheetSaveStatus
              title={props.properties.interactiveResponse.title || ''}
              onClose={() => {
                if (closeModalCallbackRef.current) {
                  closeModalCallbackRef.current();
                  closeModalCallbackRef.current = undefined;
                } else {
                  closeModal();
                }
              }}
            />
          }
        >
          <ActivitySheetInnerContent>
            <ActivitySheetInnerMax>
              {isMediaUpSm ? (
                <InteractiveSequenceLargeView
                  showAnswer={isLesson}
                  {...props}
                  isPlaying={isPlaying}
                  finishGame={finishGame}
                  onAnswerAttempt={handleSaveAttempt}
                  attemptAnswers={
                    componentAnswers?.[0]
                      ?.answer as InteractiveResponseAnswerFragment
                  }
                  setCloseModalCallback={callback => {
                    closeModalCallbackRef.current = callback;
                  }}
                />
              ) : (
                <InteractiveDragDrop>
                  <InteractiveSequenceSmallView
                    showAnswer={isLesson}
                    {...props}
                    isPlaying={isPlaying}
                    finishGame={finishGame}
                    playClick={playClick}
                    playStart={playStart}
                    playAudio={playAudio}
                    toggleAudio={toggleAudio}
                    onAnswerAttempt={handleSaveAttempt}
                    attemptAnswers={
                      componentAnswers?.[0]
                        ?.answer as InteractiveResponseAnswerFragment
                    }
                  />
                </InteractiveDragDrop>
              )}
            </ActivitySheetInnerMax>
          </ActivitySheetInnerContent>
        </ActivitySheet>
      )}
    </>
  );
};

export default InteractiveResponseStudent;
